var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Suspense, useEffect, useRef, memo, useLayoutEffect, useMemo, useState, } from 'react';
import { useQuery, useMutation, QueryClient, QueryClientProvider, } from '@tanstack/react-query';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { ErrorBoundary } from 'react-error-boundary';
import Policy from './components/Tabs/Policy/Policy';
import Coverages from './components/Tabs/Coverages/Coverages';
import QuoteInfo from './components/Tabs/QuoteInfo/QuoteInfo';
import PolicyInfo from './components/Tabs/PolicyInfo/PolicyInfo';
import PolicyInfoLastStep from './components/Tabs/PolicyInfo/PolicyInfoLastStep';
import PolicyDocuments from './components/Tabs/PolicyDocuments/index';
import PolicyChanges from './components/Tabs/PolicyChanges/index';
import ViewPolicyChanges from './components/Tabs/PolicyChanges/ViewChanges';
import QuotePending from './components/Tabs/Policy/Screens/QuotePending';
import QuoteLoading from './components/Tabs/Policy/QuoteLoading';
import api, { instance } from './api';
import { extractApplicationFormData, PREFILL_ERROR_MESSAGE } from './api/prefill';
import { InsuranceProvider } from './context/Insurance';
import Notifications from './components/Tabs/Notifications';
import Claims from './components/Tabs/Claims';
import Modal from './components/Modal';
import ErrorComponent from './components/Error';
import useConfigStore from './stores/configStore';
import useCustomerStore from './stores/customerStore';
import useSaasPlatformStore from './stores/saasPlatformStore';
import useWidgetStore, { CurrentTabEnum } from './stores/widgetStore';
import useToken from './hooks/useToken';
import useProductStore from './stores/productStore';
import useErrorHandledMutation from './hooks/useErrorHandledMutation';
import useErrorStore from './stores/errorStore';
import useLatestQuote from './hooks/useLatestQuote';
import { AxiosError } from 'axios';
import Tab from './components/Tab';
import Footer from './components/Footer';
import { MISSING_EXTERNAL_CUSTOMER_ID_ERROR, normalizeExternalCustomerID, } from './utils/customer';
import getStyles from './utils/getStyles';
import EligibilityForm from './components/Tabs/Policy/Screens/EligibilityForm';
import BusinessTypeSelection from './components/Tabs/Policy/Screens/PolicySelection/BusinessTypeSelection';
import RecommendedPolicies from './components/Tabs/Policy/Screens/PolicySelection/RecommendedPolicies';
import EligiblePolicies from './components/Tabs/Policy/Screens/PolicySelection/EligiblePolicies';
import PolicyAuditAiRecommendedPolicies from './components/Tabs/Policy/Screens/PolicySelection/PolicyAuditAiRecommendedPolicies';
var queryClient = new QueryClient();
var getPrefillErrorMessage = function (error) {
    var _a, _b, _c, _d;
    if (error instanceof AxiosError) {
        return (((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) ||
            ((_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.error[0]) ||
            error.message ||
            PREFILL_ERROR_MESSAGE);
    }
    if (error instanceof Error) {
        return error.message;
    }
    return PREFILL_ERROR_MESSAGE;
};
var App = memo(function () {
    var _a, _b, _c;
    var scrollableElementRef = useRef(null);
    var _d = useWidgetStore(), modalOpen = _d.modalOpen, currentTab = _d.currentTab, showNotification = _d.showNotification, resetWidgetState = _d.resetWidgetState, createQuoteSubmitted = _d.createQuoteSubmitted, setCreateQuoteSubmitted = _d.setCreateQuoteSubmitted, applicationDataId = _d.applicationDataId, unifiedState = _d.unifiedState;
    var _e = useConfigStore(), style = _e.style, keys = _e.keys, setConfig = _e.setConfig;
    var _f = useCustomerStore(), extCustomerID = _f.extCustomerID, prefillId = _f.prefillId, applicationFormData = _f.applicationFormData, setExternalCustomer = _f.setExternalCustomer;
    var _g = useSaasPlatformStore(), setSaasPlatformStore = _g.setSaasPlatformStore, Products = _g.Products, policyPageHeading = _g.policyPageHeading;
    var setProduct = useProductStore().setProduct;
    var errorHandledMutation = useErrorHandledMutation().errorHandledMutation;
    var _h = useToken(), tokenLoading = _h.tokenLoading, refreshToken = _h.refreshToken, pushTokenLog = _h.pushTokenLog;
    var _j = useErrorStore(), error = _j.error, setError = _j.setError, setConfigError = _j.setConfigError, configError = _j.configError;
    var latestQuote = useLatestQuote(applicationDataId);
    var hasExternalCustomerID = Boolean(extCustomerID);
    var shouldFetchPrefill = Boolean(prefillId && extCustomerID);
    var _k = useState(Boolean(prefillId)), isPrefillLoading = _k[0], setIsPrefillLoading = _k[1];
    var _l = useState(!prefillId), isPrefillReady = _l[0], setIsPrefillReady = _l[1];
    var customerQueryEnabled = hasExternalCustomerID && !tokenLoading && (!prefillId || isPrefillReady);
    useEffect(function () {
        if (!hasExternalCustomerID) {
            setIsPrefillLoading(false);
            setIsPrefillReady(false);
            setError({ error: MISSING_EXTERNAL_CUSTOMER_ID_ERROR });
            return;
        }
        if (!prefillId) {
            setIsPrefillLoading(false);
            setIsPrefillReady(true);
            setError({ error: null });
            return;
        }
        setIsPrefillLoading(true);
        setIsPrefillReady(false);
        setError({ error: null });
    }, [prefillId, hasExternalCustomerID, extCustomerID]);
    useEffect(function () {
        if (tokenLoading || !shouldFetchPrefill) {
            return;
        }
        var isCancelled = false;
        var fetchPrefill = function () { return __awaiter(void 0, void 0, void 0, function () {
            var response, prefillApplicationFormData, prefillError_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, 3, 4]);
                        return [4 /*yield*/, api.prefill.get({ prefillId: prefillId })];
                    case 1:
                        response = _a.sent();
                        prefillApplicationFormData = extractApplicationFormData(response.data, extCustomerID);
                        if (isCancelled) {
                            return [2 /*return*/];
                        }
                        setExternalCustomer({ applicationFormData: prefillApplicationFormData });
                        setIsPrefillReady(true);
                        return [3 /*break*/, 4];
                    case 2:
                        prefillError_1 = _a.sent();
                        if (isCancelled) {
                            return [2 /*return*/];
                        }
                        setIsPrefillReady(false);
                        setError({ error: getPrefillErrorMessage(prefillError_1) });
                        return [3 /*break*/, 4];
                    case 3:
                        if (!isCancelled) {
                            setIsPrefillLoading(false);
                        }
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        fetchPrefill();
        return function () {
            isCancelled = true;
        };
    }, [prefillId, shouldFetchPrefill, tokenLoading, extCustomerID]);
    var widgetFlow = useMemo(function () {
        var _a, _b;
        if (applicationFormData) {
            var policyKeys = Object.keys(applicationFormData);
            if (!policyKeys.length) {
                return;
            }
            var key = policyKeys[0];
            var applicationData_1 = applicationFormData[key];
            if (!applicationData_1) {
                return;
            }
            var findProd = (_a = Products === null || Products === void 0 ? void 0 : Products.find) === null || _a === void 0 ? void 0 : _a.call(Products, function (p) { return p.id == applicationData_1.productId; });
            return (_b = findProd === null || findProd === void 0 ? void 0 : findProd.Underwriter) === null || _b === void 0 ? void 0 : _b.widgetFlow;
        }
    }, [applicationFormData, Products]);
    var createNewQuote = useMutation(api.quote.create, {
        onSuccess: function () {
            queryClient.invalidateQueries(['customer']);
        }
    });
    var createNewQuoteNotification = useMutation(api.quote.createNotification, {
        onSuccess: function () {
            queryClient.invalidateQueries(['customer']);
        }
    });
    var createQuoteMutation = function (customerID, items, productId, quoteExternalId) {
        return errorHandledMutation(function () {
            return createNewQuote.mutateAsync({
                customerID: customerID,
                productId: productId,
                items: {
                    requiredParams: items,
                    otherParams: {}
                },
                saasId: keys.platformId,
                externalID: extCustomerID,
                quoteExternalId: quoteExternalId
            });
        });
    };
    var createQuoteNotificationMutation = function (customerID, externalId, productId, items, policyId) {
        return errorHandledMutation(function () {
            return createNewQuoteNotification.mutateAsync({
                customerID: customerID,
                productId: productId,
                items: {
                    requiredParams: items,
                    otherParams: {}
                },
                saasId: keys.platformId,
                externalID: extCustomerID,
                quoteExternalId: externalId,
                policyId: policyId
            });
        });
    };
    var customer = useQuery(['customer'], function () { return api.customer.get(extCustomerID, keys.platformId); }, {
        enabled: customerQueryEnabled,
        retry: 0,
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                if (((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) === 403) {
                    pushTokenLog({ '403 WHEN FETCHING CUSTOMER': null });
                    refreshToken().then(function () {
                        queryClient.invalidateQueries(['customer']);
                    });
                }
                return [2 /*return*/];
            });
        }); },
        onSuccess: function (res) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
            return __awaiter(this, void 0, void 0, function () {
                var usedProductIds, invalidProductIds, firstProduct, productIdKey, productId_1, baseTheme_1, root_1, newStyles;
                return __generator(this, function (_z) {
                    //@ts-ignore
                    setExternalCustomer({
                        id: (_b = (_a = res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.id
                    });
                    if (createQuoteSubmitted) {
                        setCreateQuoteSubmitted(false);
                    }
                    setSaasPlatformStore((_d = (_c = res.data) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.SaasPlatform);
                    usedProductIds = {};
                    Object.values(applicationFormData || {}).forEach(function (formData) {
                        usedProductIds[formData.productId] = true;
                    });
                    (_g = (_f = (_e = res.data) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.SaasPlatform) === null || _g === void 0 ? void 0 : _g.Products.forEach(function (prod) {
                        delete usedProductIds[prod.id];
                    });
                    invalidProductIds = Object.keys(usedProductIds);
                    if (invalidProductIds.length > 0) {
                        setConfigError(true);
                        throw new Error("You are using product IDs ".concat(invalidProductIds.map(function (id) { return "\"".concat(id, "\""); }), " which are not supported by platform you are using"));
                    }
                    firstProduct = null;
                    if (applicationFormData) {
                        productIdKey = (unifiedState === null || unifiedState === void 0 ? void 0 : unifiedState.productType)
                            ? "product".concat(unifiedState.productType.split('-')[1], "Id")
                            : 'productId';
                        productId_1 = ((_j = (_h = Object.values(applicationFormData)) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j[productIdKey]) ||
                            ((_l = (_k = Object.values(applicationFormData)) === null || _k === void 0 ? void 0 : _k[0]) === null || _l === void 0 ? void 0 : _l.productId);
                        if (productId_1) {
                            firstProduct =
                                ((_q = (_p = (_o = (_m = res.data) === null || _m === void 0 ? void 0 : _m.data) === null || _o === void 0 ? void 0 : _o.SaasPlatform) === null || _p === void 0 ? void 0 : _p.Products) === null || _q === void 0 ? void 0 : _q.find(function (v) { return v.id == productId_1; })) || null;
                        }
                    }
                    if (firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) {
                        baseTheme_1 = {
                            '--theme-primary': ((_r = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _r === void 0 ? void 0 : _r.primaryColor)
                                ? (_s = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _s === void 0 ? void 0 : _s.primaryColor
                                : style.primaryColor
                                    ? style.primaryColor
                                    : 'purple',
                            '--theme-secondary': ((_t = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _t === void 0 ? void 0 : _t.secondaryColor)
                                ? (_u = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _u === void 0 ? void 0 : _u.secondaryColor
                                : style.secondaryColor
                                    ? style.secondaryColor
                                    : 'white'
                        };
                        root_1 = document.getElementById('kayna');
                        Object.keys(baseTheme_1).forEach(function (cssVar) {
                            root_1.style.setProperty(cssVar, baseTheme_1[cssVar]);
                        });
                        newStyles = __assign(__assign({}, style), { primaryColor: ((_v = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _v === void 0 ? void 0 : _v.primaryColor)
                                ? (_w = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _w === void 0 ? void 0 : _w.primaryColor
                                : style.primaryColor
                                    ? style.primaryColor
                                    : 'purple', secondaryColor: ((_x = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _x === void 0 ? void 0 : _x.secondaryColor)
                                ? (_y = firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) === null || _y === void 0 ? void 0 : _y.secondaryColor
                                : style.secondaryColor
                                    ? style.secondaryColor
                                    : 'white' });
                        setConfig({
                            style: newStyles
                        });
                        setProduct({
                            productBranding: (firstProduct === null || firstProduct === void 0 ? void 0 : firstProduct.productBranding) || null
                        });
                    }
                    return [2 /*return*/];
                });
            });
        }
    });
    if (customer.error && ((_b = (_a = customer.error) === null || _a === void 0 ? void 0 : _a.message) === null || _b === void 0 ? void 0 : _b.includes('504'))) {
        throw new Error('Gateway Timeout Error');
    }
    useEffect(function () {
        var _a, _b;
        if (((_b = (_a = customer === null || customer === void 0 ? void 0 : customer.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.externalId) &&
            extCustomerID !== customer.data.data.externalId) {
            queryClient.clear();
            resetWidgetState();
        }
    }, [extCustomerID]);
    var getTabComponent = function (scrollableElementRef) {
        switch (currentTab) {
            case CurrentTabEnum.POLICY:
                return (React.createElement(Policy, { scrollableElementRef: scrollableElementRef, customer: customer }));
            case CurrentTabEnum.CLAIMS:
                return (React.createElement(Claims
                // primaryColor={style.primaryColor}
                // customerID={Number(extCustomer.customerId)}
                // setOpenModal={props.setOpenModal}
                // propsClaims={customerState.claims}
                // initiailzedPlatformClaims={initiailzedPlatformClaims}
                // setInitiailzedPlatformClaims={setInitiailzedPlatformClaims}
                , null));
            case CurrentTabEnum.NOTIFICATIONS:
                return React.createElement(Notifications, { show: showNotification });
            case CurrentTabEnum.QUOTE_INFO:
                return React.createElement(QuoteInfo, null);
            case CurrentTabEnum.POLICY_INFO:
                return React.createElement(PolicyInfo, null);
            case CurrentTabEnum.COVERAGES:
                return React.createElement(Coverages, { showHeader: true });
            case CurrentTabEnum.POLICY_INFO_LAST_STEP:
                return React.createElement(PolicyInfoLastStep, null);
            case CurrentTabEnum.QUOTE_PENDING:
                return React.createElement(QuotePending, null);
            case CurrentTabEnum.POLICY_CHANGES:
                return React.createElement(PolicyChanges, null);
            case CurrentTabEnum.VIEW_POLICY_CHANGES:
                return React.createElement(ViewPolicyChanges, null);
            case CurrentTabEnum.ELIGIBILITY_ENGINE:
                return React.createElement(EligibilityForm, null);
            case CurrentTabEnum.BUSINESS_TYPE_SELECTION:
                return React.createElement(BusinessTypeSelection, null);
            case CurrentTabEnum.RECOMMENDED_POLICIES:
                return React.createElement(RecommendedPolicies, null);
            case CurrentTabEnum.ELIGIBLE_POLICIES:
                return React.createElement(EligiblePolicies, null);
            case CurrentTabEnum.POLICY_AUDIT_AI_RECOMMENDED_POLICIES:
                return React.createElement(PolicyAuditAiRecommendedPolicies, null);
            case CurrentTabEnum.POLICY_DOCUMENTS:
                return React.createElement(PolicyDocuments, null);
            default:
                return React.createElement(Policy, null);
        }
    };
    React.useEffect(function () {
        var _a, _b, _c;
        var baseTheme = {
            '--theme-primary': style.primaryColor ? style.primaryColor : 'purple',
            '--theme-secondary': style.secondaryColor
                ? style.secondaryColor
                : 'white',
            '--theme-bg-color': (_a = getStyles(style.theme)) === null || _a === void 0 ? void 0 : _a.backgroundColor,
            '--theme-text-color': (_b = getStyles(style.theme)) === null || _b === void 0 ? void 0 : _b.textColor,
            '--theme-loading-color': (_c = getStyles(style.theme)) === null || _c === void 0 ? void 0 : _c.loadingColor
        };
        var root = document.getElementById('kayna');
        Object.keys(baseTheme).forEach(function (cssVar) {
            root.style.setProperty(cssVar, baseTheme[cssVar]);
        });
    }, [style.primaryColor, style.secondaryColor, style.theme]);
    if (configError) {
        throw new Error('Invalid config');
    }
    return (React.createElement("div", { className: "m-auto flex flex-col align-center justify-center relative overflow-hidden kayna-app bg-backgroundColor ".concat(modalOpen
            ? 'w-screen h-screen sm:w-[95vw] sm:h-[95vh] rounded-none sm:rounded-lg'
            : "w-[".concat(style.width, "] h-[").concat(style.height, "] rounded-3xl")), style: {
            width: !modalOpen && style.width,
            height: !modalOpen && style.height,
            border: "1px solid ".concat((_c = getStyles(style.theme)) === null || _c === void 0 ? void 0 : _c.widgetBorderColor)
        } },
        !modalOpen && (React.createElement("div", { className: "pb-2" },
            React.createElement(Suspense, { fallback: React.createElement("div", { className: "h-5", style: { backgroundColor: '#F8F8F9' } }) },
                React.createElement("ul", { className: "flex mb-0 list-none flex-row bg-backgroundColor", role: "tablist", style: {
                        width: style.width
                    } },
                    React.createElement(Tab, { name: policyPageHeading !== null && policyPageHeading !== undefined
                            ? policyPageHeading
                            : widgetFlow === 'hartford_paf'
                                ? 'Belfry Insurance Program'
                                : widgetFlow === 'briza_unified_hiscox'
                                    ? 'KwantSure'
                                    : 'Quote & Policy Overview', number: 1, isLeftAligned: !latestQuote, isHidden: false }),
                    latestQuote && (React.createElement(Tab, { number: 3, icon: faBell, disabled: !showNotification })))))),
        React.createElement("div", { className: "antialiased text-center bg-backgroundColor bg-opacity-40 relative w-full h-full flex flex-col justify-start overflow-y-scroll custom-scrollbar overflow-x-hidden", ref: scrollableElementRef, id: "containerId" }, isPrefillLoading ? React.createElement(QuoteLoading, null) : getTabComponent(scrollableElementRef)),
        !error && (React.createElement("div", { className: "w-full" },
            React.createElement(Suspense, { fallback: React.createElement("div", { className: "h-5", style: { backgroundColor: '#F8F8F9' } }) },
                React.createElement(Footer, null)))),
        React.createElement("div", { id: "widget-portal-root" })));
});
var QueryWrappedApp = function (props) {
    var _a = useConfigStore(), setConfig = _a.setConfig, setConfigV2 = _a.setConfigV2, resetConfig = _a.resetConfig, config = __rest(_a, ["setConfig", "setConfigV2", "resetConfig"]);
    var _b = useErrorStore(), resetErrorState = _b.resetErrorState, error = _b.error;
    var _c = useCustomerStore(), setExternalCustomer = _c.setExternalCustomer, applicationFormData = _c.applicationFormData, extCustomerID = _c.extCustomerID, resetRecommendationEngine = _c.resetRecommendationEngine, resetEligibilityEngine = _c.resetEligibilityEngine, resetPolicyAuditEngine = _c.resetPolicyAuditEngine, resetPolicyAuditAiEngine = _c.resetPolicyAuditAiEngine, resetPrefillId = _c.resetPrefillId;
    var widgetConfig = useWidgetStore();
    useEffect(function () {
        var _a, _b, _c, _d;
        var isDefaultOpen = Boolean((_a = props.customer) === null || _a === void 0 ? void 0 : _a.defaultModalOpen);
        var nextCustomerState = __assign(__assign({}, props.customer), { extCustomerID: normalizeExternalCustomerID((_b = props.customer) === null || _b === void 0 ? void 0 : _b.extCustomerID), applicationFormData: ((_c = props.customer) === null || _c === void 0 ? void 0 : _c.prefillId)
                ? {}
                : ((_d = props.customer) === null || _d === void 0 ? void 0 : _d.applicationFormData) || {} });
        setExternalCustomer(nextCustomerState);
        if (isDefaultOpen) {
            widgetConfig.setModalOpen(isDefaultOpen);
        }
        return function () {
            resetRecommendationEngine();
            resetEligibilityEngine();
            resetPolicyAuditEngine();
            resetPolicyAuditAiEngine();
            resetPrefillId();
        };
    }, [props.customer]);
    useLayoutEffect(function () {
        queryClient.clear();
        resetErrorState();
        resetConfig();
        widgetConfig.resetState();
        instance.defaults.headers.common['authorization'] = null;
    }, [props.config.keys.token]);
    useEffect(function () {
        resetErrorState();
        var newConfig = __assign(__assign({}, props.config.style), { theme: props.config.style.theme ? props.config.style.theme : 'default' });
        var _a = props.config.keys, token = _a.token, restConfig = __rest(_a, ["token"]);
        setConfig({
            style: newConfig,
            keys: __assign(__assign({}, restConfig), { initToken: token, hmacData: null })
        });
        // return () => {
        //   setConfig({
        //     keys: { ...props.config.keys, hmacData: null, token: null },
        //   });
        // };
    }, [props.config]);
    return (React.createElement(QueryClientProvider, { client: queryClient },
        React.createElement(InsuranceProvider, null,
            React.createElement(ErrorBoundary, { onError: function (e) {
                    var details = {
                        widgetConfig: __assign(__assign({}, config), { params: applicationFormData }),
                        state: widgetConfig,
                        error: {
                            message: e.message,
                            stack: e.stack
                        }
                    };
                    api.crashReports.submitReport({
                        externalId: extCustomerID,
                        saasPlatformId: config.keys.platformId,
                        details: details
                    });
                }, fallback: React.createElement(ErrorComponent, { style: props.config.style, error: error }) },
                React.createElement(Modal, null,
                    React.createElement(App, null))))));
};
export default QueryWrappedApp;
